import React, { useState, useMemo } from 'react';
import BottomNavigation from 'src/common/refactoredComponents/bottomNavigation/BottomNavigation';
import Button from 'src/common/refactoredComponents/button/Button';
import { ButtonStyles } from 'src/constants/buttonStyles';
import { ReportUsageButtonContainer } from './ReportUsageFooter.styled';
import { sizes } from 'src/styles/Media.styled';
import { isIpad, logErrorToConsole } from 'src/utils/util';
import { useTranslation } from 'react-i18next';
import { useEffectWithErrorLogging } from 'src/utils/useEffectWithErrorLogging';

const ReportUsageFooter = ({
    footerButtonStatuses,
    cancelBtnClickHandler,
    saveExitBtnClickHandler,
    replenishBtnClickHandler,
    procedureBtnClickHandler,
    unidInfoBtnClickHandler,
    ORreportBtnClickHandler,
    reviewBtnClickHandler,
    submitBtnClickHandler,
    defaultBtnClickHandler,
    isPriceOverride,
    applyBtnClickHandler,
    zIndex = 2000,
    customStyle = {
        position: 'fixed',
        width: {
            standard: 'calc(100% - 5rem)',
            portraitDevice: '100%',
            smallDevice: '100%',
        },
        padding: '5px',
        inPaddingVertical: '5px',
    },
    poFlag,
    showReportPayment,
    nextBtnClickHandler,
    reportPaymentScreenName,
    myCaseResourceType,
    setProgressBarClicked,
    reportUsage,
    isLanguageChanged,
    saveAndExitClickFlag,
}) => {
    const { t, ready } = useTranslation();
    
    // Add state for window width that updates on resize
    const [winWidth, setWinWidth] = useState(window.innerWidth);
    
    useEffectWithErrorLogging(() => {
        const handleResize = () => {
            setWinWidth(window.innerWidth);
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    const isDesktopView = winWidth >= sizes.mediumDevice;

    // Memoize translated button names to prevent re-calculation
    const translatedButtonNames = useMemo(() => ({
        cancel: t('reportUsage.reportUsageFooterbuttons.cancelButton'),
        saveExit: t('reportUsage.saveExit'),
        apply: t('orders.orderListFilters.applyButton'),
        replenish: t('reportUsage.reportUsageFooterbuttons.replenishButton'),
        procedure: t('reportUsage.progressBar.procedure'),
        unidInfo: t('reportUsage.progressBar.UNiDInfo'),
        review: t('reportUsage.reportUsageFooterbuttons.reviewButton'),
        submit: t('noUsageToReport.submitButton'),
        next: t('scheduleSurgery.surgery.SurgeryFooter.nextBtn'),
    }), [t]);

    const buttonClickHandler = (btnName) => {
        switch (btnName) {
            case translatedButtonNames.cancel:
                return cancelBtnClickHandler();
            case translatedButtonNames.saveExit:
                return saveExitBtnClickHandler();
            case translatedButtonNames.apply:
                return applyBtnClickHandler();
            case translatedButtonNames.replenish:
                return replenishBtnClickHandler();
            case translatedButtonNames.procedure:
                return procedureBtnClickHandler();
            case translatedButtonNames.unidInfo:
                return unidInfoBtnClickHandler();
            case 'OR Report':
                return ORreportBtnClickHandler();
            case translatedButtonNames.review:
                return reviewBtnClickHandler();
            case translatedButtonNames.submit:
                return submitBtnClickHandler();
            case translatedButtonNames.next:
                return nextBtnClickHandler();
            default:
                return defaultBtnClickHandler();
        }
    };
    
    // Don't render until translations are ready
    if (!ready) {
        return null;
    }
    
    return (
        <>
            <BottomNavigation
                justifyContent="space-around"
                moblieJustifyContent="space-evenly"
                padding={customStyle.padding}
                right={'0'}
                position={customStyle.position}
                width={customStyle.width}
                zIndex={zIndex}
                bottom={poFlag && isIpad() && showReportPayment ? '1px' : '0'}
            >
                {
                    <ReportUsageButtonContainer
                        smallJustifyContent={'center'}
                        justifyContent={''}
                        style={{
                            paddingRight:
                                showReportPayment && isIpad() && '50px',
                        }}
                    >
                        {footerButtonStatuses?.map((btn) => {
                            // Calculate show condition outside JSX for clarity
                            const shouldShow = myCaseResourceType === 'reportPayment'
                                ? !(reportPaymentScreenName !== 'Review' && btn.btnName === translatedButtonNames.submit) && btn.btnVisibility
                                : btn.btnVisibility;
                            // Calculate submit button specific styles
                            const isSubmitButton = btn.btnName === translatedButtonNames.submit;
                            const isMobileDevice = winWidth <= sizes.mobileDevice;

                            // Save & Exit is not applicable for Add Report Usage (additional usage)
                            const isBtnDisabled = btn.isBtnDisable
                                || (myCaseResourceType === 'additionalUsage'
                                    && btn.btnName === translatedButtonNames.saveExit)
                                    || (btn.btnName !== translatedButtonNames.cancel && saveAndExitClickFlag);
                            const shouldApplySubmitStyles = !isBtnDisabled && isMobileDevice && isSubmitButton;
                            
                            // Calculate button width
                            const getButtonWidth = () => {
                                if (isPriceOverride) return '160px';
                                if (isDesktopView) return '140px';
                                if (isLanguageChanged && btn.btnName === translatedButtonNames.saveExit) return '117px';
                                if (isLanguageChanged && btn.btnName === translatedButtonNames.procedure) return '130px';
                                return '110px';
                            };
                            
                            return (
                                <Button
                                    id={`reportUsage-footer-${btn.btnName}`}
                                    key={btn.btnName}
                                    show={shouldShow}
                                    disabled={isBtnDisabled}
                                    backgroundColor={shouldApplySubmitStyles ? '#E5047E' : undefined}
                                    color={shouldApplySubmitStyles ? '#FFFFFF' : undefined}
                                    variant={
                                        isBtnDisabled
                                            ? ButtonStyles.disabled
                                            : btn.btnType === 'primary'
                                                ? ButtonStyles?.primary
                                                : btn.btnType === 'secondary'
                                                    ? ButtonStyles?.secondary
                                                    : btn.btnType === 'cancel'
                                                        ? ButtonStyles?.cancel
                                                        : undefined
                                    }
                                    width={getButtonWidth()}
                                    height={'38px'}
                                    clicked={() => {
                                        try {
                                            if (reportUsage) {
                                                setProgressBarClicked(false);
                                            }
                                            buttonClickHandler(btn.btnName);
                                        } catch (error) {
                                            logErrorToConsole(error);
                                        }
                                    }}
                                    margin={'10px 6px'}
                                    mobileMargin={'5px'}
                                    fontSize={'14px'}
                                    borderRadius={'25px'}
                                    WhiteSpace={'nowrap'}
                                    throttle={5000}
                                >
                                    {btn.btnName}
                                </Button>
                            );
                        })}
                    </ReportUsageButtonContainer>
                }
            </BottomNavigation>
        </>
    );
};

export default ReportUsageFooter;
